import React from "react";
import { Typography, Form } from "antd";
import { AddPricingTooltip } from "./add-pricing-tooltip";
import { AlterCalculatedPriceTooltip } from "./alter-calculated-price-tooltip";
import styles from "./fee-range.module.css";
import {
  GpmThreshold,
  Range,
  PricingType,
  PricingTypeLabels,
  PricingLevel,
  Gpm,
  PriceRange,
} from "../../data-types";
import { SidebarPriceInputRange } from "./sidebar-price-input-range";
import { Gpm as GpmComponent } from "./gpm";
import { SidebarPriceInputSingle } from "./sidebar-price-input-single";
import { priceOrPriceRangeOrPricingSummaryOrPricingSummaryRangeIsZero } from "./sum-legal-fee-costs";
import { GpmSlider } from "../gpm-slider";
import { useGpmSettingsCheck } from "../../../hooks";

const {
  number: numberClassName,
  hyphen: hyphenClassName,
  ["gpm-slider-only"]: gpmSliderOnlyClassName,
  ["gpm-slider-with-percentage"]: gpmSliderWithPercentageClassName,
} = styles as any;

export type FeeRangeProps = {
  gpm: Gpm;
  gpmThreshold: GpmThreshold;
  onChosenPriceChange: (arg0: PriceRange) => void;
  onFixedPriceAlternativeChange: (arg0: number) => void;
  fixedPriceAlternative: number | null;
  suggested?: Range;
  chosen: Range;
  pricingType: PricingType;
  pricingLevel: PricingLevel;
  currencySymbol: string;
  isFeeEarnerLevelGpm: boolean;
};

export const FeeRange: React.FC<FeeRangeProps> = ({
  gpm,
  gpmThreshold,
  onChosenPriceChange,
  onFixedPriceAlternativeChange,
  fixedPriceAlternative,
  suggested,
  chosen,
  pricingType,
  pricingLevel,
  currencySymbol,
  isFeeEarnerLevelGpm,
}) => {
  const suggestedDisplay = suggested
    ? {
        lower: suggested?.lower !== null ? suggested?.lower.toLocaleString() : "",
        upper: suggested?.upper !== null ? suggested?.upper.toLocaleString() : "",
      }
    : { lower: "", upper: "" };

  const chosenIsZero = priceOrPriceRangeOrPricingSummaryOrPricingSummaryRangeIsZero(chosen);

  if (pricingType === PricingType.None) return null;

  const { showGpm, showGpmSlider } = useGpmSettingsCheck();

  return (
    <>
      <Form.Item label="Pricing">{PricingTypeLabels[pricingType]}</Form.Item>
      <Form.Item label="Calculated">
        {pricingLevel === PricingLevel.FeeEarner ? (
          <AlterCalculatedPriceTooltip>
            <Typography.Text className={numberClassName}>
              {currencySymbol} {suggestedDisplay.lower}
            </Typography.Text>
            <Typography.Text style={{ width: hyphenClassName }}> - </Typography.Text>
            <Typography.Text className={numberClassName}>
              {currencySymbol} {suggestedDisplay.upper}
            </Typography.Text>
          </AlterCalculatedPriceTooltip>
        ) : (
          <AddPricingTooltip>Not Available</AddPricingTooltip>
        )}
      </Form.Item>
      <Form.Item label="User Defined">
        <SidebarPriceInputRange
          currencySymbol={currencySymbol}
          onChange={onChosenPriceChange}
          chosen={chosen}
          phaseLevelPricing={pricingLevel === PricingLevel.Phase}
          topDownPricing={pricingType === PricingType.TopDown}
          disabled={pricingLevel !== PricingLevel.Matter}
        />
      </Form.Item>
      {!chosenIsZero ? (
        <Form.Item label="Fixed Fee Alternative" labelCol={{ sm: { span: -1, offset: 0 } }}>
          <SidebarPriceInputSingle
            phaseLevelPricing={pricingLevel === PricingLevel.Phase}
            topDownPricing={pricingType === PricingType.TopDown}
            currencySymbol={currencySymbol}
            onChange={(newValue) => {
              if (newValue !== fixedPriceAlternative) {
                onFixedPriceAlternativeChange(newValue);
              }
            }}
            chosen={fixedPriceAlternative}
            disabled={false}
          />
        </Form.Item>
      ) : (
        ""
      )}
      {showGpm && (
        <Form.Item label="GPM" labelCol={{ span: -1, offset: 0 }}>
          <GpmComponent
            gpm={gpm}
            gpmThreshold={gpmThreshold}
            isFeeEarnerLevelGpm={isFeeEarnerLevelGpm}
          />
        </Form.Item>
      )}
      {!(showGpm && gpm == null) &&
        showGpmSlider &&
        gpmThreshold.gpmDangerThreshold &&
        gpmThreshold.gpmWarningThreshold && (
          <Form.Item
            label={showGpm ? undefined : "GPM"}
            className={showGpm ? gpmSliderWithPercentageClassName : ""}
          >
            <div className={showGpm ? "" : gpmSliderOnlyClassName}>
              <GpmSlider
                showGpm={showGpm}
                gpmThreshold={gpmThreshold}
                gpm={gpm}
                isFeeEarnerLevelGpm={isFeeEarnerLevelGpm}
              />
            </div>
          </Form.Item>
        )}
    </>
  );
};
